import React, { useState, useEffect } from 'react';
import type { ProductDto } from '../../services/api';

export type ProductFormData = {
  sku: string;
  product_type: string;
  product_name: string;
  category: string;
  unit_of_measure: string;
  description: string;
  cost_price: string;
  selling_price: string;
  tax_rate: string;
  qty_on_hand: string;
  reorder_level: string;
  reorder_qty: string;
  status: string;
};

const initialForm: ProductFormData = {
  sku: 'SKU-001',
  product_type: 'Inventory Item',
  product_name: '',
  category: '',
  unit_of_measure: 'Each',
  description: '',
  cost_price: '0',
  selling_price: '0',
  tax_rate: '0',
  qty_on_hand: '0',
  reorder_level: '0',
  reorder_qty: '0',
  status: 'Active',
};

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProductFormData) => Promise<void>;
  editProduct?: ProductDto | null;
}

export default function AddProductModal({
  open,
  onClose,
  onSubmit,
  editProduct,
}: AddProductModalProps) {
  const [form, setForm] = useState<ProductFormData>(initialForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editProduct) {
      setForm({
        sku: editProduct.sku,
        product_type: editProduct.product_type,
        product_name: editProduct.product_name,
        category: editProduct.category || '',
        unit_of_measure: editProduct.unit_of_measure || 'Each',
        description: editProduct.description || '',
        cost_price: String(editProduct.cost_price),
        selling_price: String(editProduct.selling_price),
        tax_rate: String(editProduct.tax_rate),
        qty_on_hand: String(editProduct.qty_on_hand),
        reorder_level: String(editProduct.reorder_level),
        reorder_qty: String(editProduct.reorder_qty),
        status: editProduct.status || 'Active',
      });
    } else {
      setForm(initialForm);
    }
  }, [editProduct, open]);

  const isEdit = !!editProduct;
  const title = isEdit ? 'Edit Product' : 'Add New Product';
  const submitLabel = isEdit ? 'Save Product' : 'Create Product';

  const update = (field: keyof ProductFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!form.product_name.trim()) {
      setErrors({ product_name: 'Product name is required.' });
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form);
      setForm(initialForm);
      onClose();
    } catch (err: unknown) {
      const res = err as { errors?: Record<string, string[]> };
      if (res?.errors) {
        const flat: Record<string, string> = {};
        Object.entries(res.errors).forEach(([k, v]) => {
          if (Array.isArray(v) && v[0]) flat[k] = v[0];
        });
        setErrors(flat);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 id="modal-title" className="text-xl font-bold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU / Part Number</label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={(e) => update('sku', e.target.value)}
                  placeholder="SKU-001"
                  className={`w-full px-3 py-2 rounded-lg border text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${
                    errors.sku ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                {errors.sku && <p className="mt-1 text-sm text-red-600">{errors.sku}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                <select
                  value={form.product_type}
                  onChange={(e) => update('product_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                >
                  <option value="Inventory Item">Inventory Item</option>
                  <option value="Service">Service</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.product_name}
                onChange={(e) => update('product_name', e.target.value)}
                placeholder="Product name"
                className={`w-full px-3 py-2 rounded-lg border text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${
                  errors.product_name ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              {errors.product_name && (
                <p className="mt-1 text-sm text-red-600">{errors.product_name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => update('category', e.target.value)}
                  placeholder="e.g., Electronics"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                <select
                  value={form.unit_of_measure}
                  onChange={(e) => update('unit_of_measure', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                >
                  <option value="Each">Each</option>
                  <option value="Box">Box</option>
                  <option value="Pack">Pack</option>
                  <option value="Kg">Kg</option>
                  <option value="L">L</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Product description"
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none resize-y"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (₱)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.cost_price}
                  onChange={(e) => update('cost_price', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price (₱)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.selling_price}
                  onChange={(e) => update('selling_price', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.tax_rate}
                  onChange={(e) => update('tax_rate', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qty on Hand</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.qty_on_hand}
                  onChange={(e) => update('qty_on_hand', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.reorder_level}
                  onChange={(e) => update('reorder_level', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Qty</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.reorder_qty}
                onChange={(e) => update('reorder_qty', e.target.value)}
                placeholder="0"
                className="w-full max-w-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              />
            </div>

            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => update('status', e.target.value)}
                  className="w-full max-w-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-60 transition-colors"
            >
              {saving ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
